import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

// Company logo URL — transparent PNG hosted in /public
const LOGO_URL = "/sawariya-logo.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/leadership', label: 'Leadership' },
    { path: '/brands', label: 'Brands' },
    { path: '/products', label: 'Products' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-navy-dark py-2'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo - Prominent with highlight glow */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center group"
          >
            <div className="logo-spotlight">
              <img 
                src={LOGO_URL} 
                alt="Sawariya Global Exports LLP" 
                className="logo-image h-16 md:h-24 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-link font-body text-sm tracking-widest uppercase transition-colors duration-300 ${
                  isActive(link.path) ? 'is-active' : ''
                } ${
                  isScrolled
                    ? isActive(link.path)
                      ? 'text-navy'
                      : 'text-gray-600 hover:text-navy'
                    : isActive(link.path)
                    ? 'text-gold'
                    : 'text-white/90 hover:text-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-get-quote"
              className={`group relative inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm tracking-[0.18em] uppercase font-semibold ${
                isScrolled
                  ? 'cta-quote-scrolled text-white'
                  : 'cta-quote text-navy-dark'
              }`}
            >
              <span className="relative z-10">Get Quote</span>
              <ArrowRight size={16} className="cta-arrow relative z-10" strokeWidth={2.5} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors ${
              isScrolled ? 'text-navy' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-cream-muted animate-fade-in">
            <div className="flex flex-col py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-6 py-4 font-body text-sm tracking-widest uppercase transition-colors ${
                    isActive(link.path)
                      ? 'text-navy bg-cream-muted'
                      : 'text-gray-600 hover:text-navy hover:bg-cream-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-6 pt-4">
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="cta-quote-scrolled flex items-center justify-center gap-2 w-full text-white py-3 rounded-full text-sm tracking-[0.18em] uppercase font-semibold"
                >
                  <span className="relative z-10">Get Quote</span>
                  <ArrowRight size={16} className="cta-arrow relative z-10" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
